/**
 * One calendar event, five ways to save it.
 *
 * The confirmation page shipped a single "Add to Google Calendar" button whose
 * `details` and `location` were fixed strings that said the call link was "in
 * your confirmation email". So the entry a prospect saved knew only that a call
 * existed: no Zoom link, nothing to click at the moment it fires. Owner: "it
 * doesn't actually add the call details. It just adds that there is a call."
 * And Google is not the only calendar anyone uses.
 *
 * Every builder here takes the same `CalendarEvent`, so a page cannot offer one
 * provider a richer entry than another, and the `.ics` used for the download
 * button is the same text the confirmation email attaches.
 */

export type CalendarEvent = {
  title: string
  /** Plain text. The Zoom/meeting link belongs in here AND in `location`. */
  description: string
  location: string
  /** ISO 8601, UTC. */
  start: string
  end: string
}

/** UTC ISO to the compact YYYYMMDDTHHMMSSZ stamp every calendar URL expects. */
function stamp(iso: string): string | null {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z")
}

function stamps(event: CalendarEvent): { start: string; end: string } | null {
  const start = stamp(event.start)
  const end = stamp(event.end)
  if (!start || !end) return null
  return { start, end }
}

export function googleCalendarUrl(event: CalendarEvent): string | null {
  const s = stamps(event)
  if (!s) return null
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${s.start}/${s.end}`,
    details: event.description,
    location: event.location,
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

/** Outlook.com and Office 365 share one compose endpoint, on two hosts. */
function outlookUrl(event: CalendarEvent, host: string): string | null {
  const start = new Date(event.start)
  const end = new Date(event.end)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title,
    startdt: start.toISOString(),
    enddt: end.toISOString(),
    body: event.description,
    location: event.location,
  })
  return `https://${host}/calendar/0/deeplink/compose?${params.toString()}`
}

export const outlookLiveUrl = (event: CalendarEvent) => outlookUrl(event, "outlook.live.com")
export const office365Url = (event: CalendarEvent) => outlookUrl(event, "outlook.office.com")

export function yahooCalendarUrl(event: CalendarEvent): string | null {
  const s = stamps(event)
  if (!s) return null
  const params = new URLSearchParams({
    v: "60",
    title: event.title,
    st: s.start,
    et: s.end,
    desc: event.description,
    in_loc: event.location,
  })
  return `https://calendar.yahoo.com/?${params.toString()}`
}

/** RFC 5545 escaping: commas, semicolons and backslashes are field separators,
 * and a literal newline ends the property. Getting this wrong does not error,
 * it silently truncates the description at the first comma. A lone CR ends a
 * content line the same way LF does, so it is folded into `\n` as well. */
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\r|\n/g, "\\n")
}

/** Folds a property to the 75-octet line limit the spec requires. Apple Mail
 * and Outlook both tolerate long lines; Google Calendar's importer does not
 * reliably, and a folded line is correct everywhere. */
function foldIcsLine(line: string): string {
  if (line.length <= 75) return line
  const parts = [line.slice(0, 75)]
  let rest = line.slice(75)
  while (rest.length > 74) {
    parts.push(` ${rest.slice(0, 74)}`)
    rest = rest.slice(74)
  }
  if (rest) parts.push(` ${rest}`)
  return parts.join("\r\n")
}

/**
 * The `.ics` body. `uid` must be stable for a given booking so a reschedule
 * replaces the entry instead of adding a second one, which is why it comes from
 * the Cal booking uid rather than being generated per request.
 *
 * `stampedAt` is injected rather than read from the clock so the same booking
 * renders byte-identical on every request, which keeps the emailed attachment
 * and the downloaded file the same document.
 */
export function buildIcs(event: CalendarEvent, uid: string, stampedAt: string): string | null {
  const s = stamps(event)
  const dtstamp = stamp(stampedAt)
  if (!s || !dtstamp) return null

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Cold Approach//Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${escapeIcsText(uid)}@coldapproach.io`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${s.start}`,
    `DTEND:${s.end}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    `DESCRIPTION:${escapeIcsText(event.description)}`,
    `LOCATION:${escapeIcsText(event.location)}`,
    "STATUS:CONFIRMED",
    // A reminder on the entry itself, so the calendar nudges him even if our
    // own 15-minute reminder email is missed or filtered.
    "BEGIN:VALARM",
    "TRIGGER:-PT15M",
    "ACTION:DISPLAY",
    "DESCRIPTION:Your Cold Approach call starts in 15 minutes",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ]
  // CRLF is required by the spec, and Outlook is the one that actually enforces it.
  return lines.map(foldIcsLine).join("\r\n")
}
